import { formatDate } from "@/lib/utils";
import React from "react";
import PropTypes from "prop-types";
import Modal from "@/components/ui/modal";
import { useQuery } from "@tanstack/react-query";
import apiClient from "../../../api/apiClient";
import { API_ENDPOINTS } from "../../../api/header";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Package,
  Store,
  Calendar,
  DollarSign,
  Hash,
  CheckCircle2,
  AlertCircle,
  Clock,
  User,
  FileText,
} from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";

const BatchDetailsModal = ({ open, onOpenChange, batchId }) => {
  const { data: batch, isLoading } = useQuery({
    queryKey: ["batch-details", batchId],
    queryFn: async () => {
      const res = await apiClient.get(
        API_ENDPOINTS.INVENTORY.BATCH_DETAILS(batchId),
        {
          params: { with: "product,store,creator" },
        },
      );
      return res.data;
    },
    enabled: !!batchId && open,
  });

  const infoItem = (icon, label, value) => (
    <div className="flex items-start gap-3 py-2">
      <div className="mt-0.5 p-1 rounded-full bg-primary/10 text-primary">
        {icon}
      </div>
      <div>
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
          {label}
        </p>
        <div className="text-sm font-semibold">{value || "-"}</div>
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Batch Details"
      icon={Package}
      maxWidth="max-w-2xl"
    >
      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
          <Separator />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : batch ? (
        <div className="space-y-6">
          {/* Header Summary */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border">
            <div>
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Hash className="h-5 w-5 text-primary" />
                {batch.batch_number}
              </h3>
              <p className="text-sm text-muted-foreground">
                {batch.product?.name}
              </p>
            </div>
            <div className="text-right">
              <Badge
                variant={batch.is_active ? "success" : "destructive"}
                className="mb-1"
              >
                {batch.is_active ? "Active" : "Inactive"}
              </Badge>
              <p className="text-2xl font-black text-primary">
                {batch.quantity}
              </p>
              <p className="text-[10px] uppercase font-bold text-muted-foreground">
                In Stock
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="text-[11px] font-bold uppercase tracking-widest text-primary/70 border-b pb-1">
                Primary Information
              </h4>
              {infoItem(
                <Store size={14} />,
                "Store Location",
                batch.store?.name,
              )}
              {infoItem(
                <DollarSign size={14} />,
                "Unit Cost",
                `€${parseFloat(batch.unit_cost).toFixed(2)}`,
              )}
              {infoItem(
                <Calendar size={14} />,
                "Expiry Date",
                batch.expiry_date
                  ? formatDate(new Date(batch.expiry_date))
                  : "No Expiry",
              )}
            </div>

            <div className="space-y-4">
              <h4 className="text-[11px] font-bold uppercase tracking-widest text-primary/70 border-b pb-1">
                Lifecycle Info
              </h4>
              {infoItem(
                <Clock size={14} />,
                "Created At",
                formatDate(new Date(batch.created_at)),
              )}
              {infoItem(
                <User size={14} />,
                "Created By",
                batch.creator?.name || "System",
              )}
              {infoItem(
                <FileText size={14} />,
                "Reference",
                batch.reference_id
                  ? `REF-${batch.reference_id}`
                  : "Direct Entry",
              )}
            </div>
          </div>

          <Separator />

          {/* Additional Info / Status Messaging */}
          <div className="rounded-md border p-4 bg-muted/10">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-amber-500 shrink-0" />
              <div>
                <h5 className="text-sm font-bold uppercase tracking-wide">
                  Stock Management Note
                </h5>
                <p className="text-xs text-muted-foreground mt-1">
                  This batch is tracked using{" "}
                  {batch.product?.tracking_type === "standard"
                    ? "Standard (Batch)"
                    : "Serial"}{" "}
                  tracking. Sales will prioritize oldest batches (FIFO) by
                  default unless manually selected.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-10">
          <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">
            Batch information not found.
          </p>
        </div>
      )}
    </Modal>
  );
};

BatchDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  batchId: PropTypes.string,
};

export default BatchDetailsModal;
